import { subscribe, emit, events } from './eventManager';

/**
 * Last result per element id.
 *
 * The event manager keeps a single last-emit payload per event name, and for
 * RELATED_RESOURCES that payload belongs to whichever component emitted last.
 * Replaying it would therefore tell a late subscriber about another element,
 * so this map holds one payload per id and replays that instead (subscribe is
 * called with `{ replay: false }`).
 */
const lastResults = new Map();

/**
 * This class is used to emit events for consumers using the component as a web component.
 * If the component is used internally within the universal components,
 * it can be consumed as a Preact component and does not require event emission.
 * Web components do not allow callbacks as props, therefore we need both options (callback and event emissions).
 *
 * @param {id} - An identifier used by the subscriber to tell if the result is for them
 * @param {count} - The count of related resources received
 *
 */
function resultsReceived(id, count) {
  let event = {
    resultCount: count
  };

  lastResults.set(id, event);
  emit(events.RELATED_RESOURCES, event, id);
}

/**
 * This is a function to subscribe to the results received for related resources
 *
 * @param {function} callback - A callback function as a parameter.
 * @return {object} - An object consisting { id: number, value: number}
 *
 * @example
 *
 *     subscribeToRelatedResources(callback)
 */
function subscribeToRelatedResources(callback) {
  const unsubscribe = subscribe(events.RELATED_RESOURCES, callback, { replay: false });

  // Components that already reported before this subscriber mounted must still
  // be replayed, otherwise a wrapper that starts hidden would stay hidden.
  lastResults.forEach((event, id) => callback(event, id));

  return unsubscribe;
}

export default {
  resultsReceived,
  subscribeToRelatedResources
};
